<template>
  <div class='lt-fyf-result-page'>
    <!-- Chosen Retirement Living Standard banner -->
    <div class='lt-fyf-result-page_standard-chosen'>
      <div class='standard-values standard-values--minimum'>
        <span class='standard-values__text'>{{ standards.minimum.text }} </span>
        <span class='standard-values__figure'>{{ config.headlineFigures[isCohabiting ? 'cohabiting' : 'single'].minimum | currency(config.currencySymbol) }}</span>
      </div>
      <div class='standard-values standard-values--moderate'>
        <span class='standard-values__text'>{{ standards.moderate.text }} </span>
        <span class='standard-values__figure'>{{ config.headlineFigures[isCohabiting ? 'cohabiting' : 'single'].moderate | currency(config.currencySymbol) }}</span>
      </div>
      <div class='standard-values standard-values--maximum'>
        <span class='standard-values__text'>{{ standards.comfortable.text }} </span>
        <span class='standard-values__figure'>{{ config.headlineFigures[isCohabiting ? 'cohabiting' : 'single'].comfortable | currency(config.currencySymbol) }}</span>
      </div>
    </div>

    <!-- Chosen Retirement Living Standard arrow -->
    <div class='target-bar'>
      <div
        class='target-triangle'
        :style="{ left: `${triangleBarLeftCSSValue}%`, transform: `translateX(-${triangleBarLeftCSSValue}%)`}"
      ></div>
    </div>

    <div class='lt-fyf-result-page_content_container'>
      <ProgressBar class='lt-fyf-progress-bar--large' />

      <!-- Initial overview -->
      <div class='lt-fyf-result-page_top_content'>
        <div class='lt-fyf-result-overview'>
          <div class='lt-fyf-result-overview_left lt-fyf-result-page_columns_text'>
            <p class='lt-fyf-result-page_top_content_main-para'>
              <RenderTemplate :string="copy.target" />
            </p>
          </div>
          <div class='lt-fyf-result-overview_center'>
            <figure class="lt-fyf-result-page_target_figure">
              <AnimatedNumber
                :value='targetIncome | roundToNearestHundred'
                :duration='0.5'
                :prefix='config.currencySymbol'
              />
              a year
            </figure>
          </div>
          <div class='lt-fyf-result-overview_right'>
            <button aria-label="Read more about how this is calculated" class="lt-fyf-result-page_target_button" @click="handleReadMoreClick('readMoreTargetVisible', 'readMoreTargetContent')">
              Read {{ readMoreTargetVisible ? 'less' : 'more' }}
            </button>
          </div>
        </div>

        <!-- Read More box -->
        <div class='lt-fyf-result-page_columns'>
          <div class='lt-fyf-result-page_columns_left'>
            <transition name="height">
              <div
                class='lt-fyf-result-page_read-more_target_content'
                v-if="readMoreTargetVisible"
                aria-live="polite"
                tabindex="1"
                ref="readMoreTargetContent"
              >
                <hr class='lt-fyf-result-page_seperator' />
                <p>
                  <RenderTemplate :string="copy.readMoreTarget" />
                </p>
              </div>
            </transition>
          </div>

          <div class='lt-fyf-result-page_columns_right read-more-container'>
          </div>
        </div>
      </div>

      <!-- Additional information -->
      <div class='lt-fyf-result-page_content'>
        <div class='lt-fyf-result-page_columns'>
          <div class='lt-fyf-result-page_columns_left middle-box'>
            <div class='lt-fyf-result-page_inputs'>
              <h3 class='lt-fyf-result-page_inputs_header'>
                <RenderTemplate :string="copy.inputsHeader" />
              </h3>
              <div :class="['lt-fyf-pd-field', isAgeValid ? '' : 'lt-fyf-pd-field--is-error']">
                <label for='lt-fyf-age'>What's your age?</label>
                <VueAutonumeric
                  id='lt-fyf-age'
                  v-model='age'
                  :options='currentAgeAutoNumericOpts'
                  pattern="[0-9]*"
                />
              </div>

              <div :class="['lt-fyf-pd-field', isCurrentBalanceValid ? '' : 'lt-fyf-pd-field--is-error']">
                <label for='lt-fyf-balance'>{{ isCohabiting ? 'Pension savings already built up by you and your partner?' : 'Pension savings already built up?' }}</label>

                <VueAutonumeric
                  id='lt-fyf-balance'
                  v-model='currentBalance'
                  :options="currentBalanceAutoNumericOpts"
                  pattern="[0-9]*"
                />
              </div>

              <div v-if="config.canChangeRetirementAge" :class="['lt-fyf-pd-field', isRetirementAgeValid ? '' : 'lt-fyf-pd-field--is-error']">
                <label for='lt-fyf-ret-age'>Age you want to retire at?</label>
                <VueAutonumeric
                  id='lt-fyf-ret-age'
                  v-model='retirementAge'
                  :options='retirementAgeAutoNumericOpts'
                  pattern="[0-9]*"
                />
              </div>

              <div v-else class="lt-fyf-pd-field">
                <label for='lt-fyf-ret-age'>We'll assume you retire at age {{ retirementAge }}</label>
              </div>

              <div class='lt-fyf-result-page_target-pm' v-if="!isOnTrack">
                <RenderTemplate class="lt-fyf-result-page_target-pm_text" :string="copy.figureAmountNeeded" />
                <div class='large-arrow'></div>
                <figure class="lt-fyf-result-page_target-pm_figure">
                  <AnimatedNumber
                    :value='resultPerMonth'
                    :duration='0.8'
                    :prefix='config.currencySymbol'
                    v-if='areInputsValid'
                  />
                  <span>per month</span>
                  <template v-if='!areInputsValid'>
                    -
                  </template>
                </figure>
              </div>

              <div class='lt-fyf-result-page_target-pm' v-else>
                <RenderTemplate :string="copy.isOnTrack" />
              </div>


              <transition name="height">
                <div
                  class='lt-fyf-result-page_read-more_amount-needed_content'
                  aria-live="polite"
                  tabindex="-1"
                  ref="readMoreAmountContent"
                >
                  <RenderTemplate :string="copy.readMoreAmountNeeded" />
                </div>
              </transition>

              <button
                class='refresh-button'
                @click='reload'
              >
                Start Over
              </button>
            </div>
          </div>

          <div class='lt-fyf-result-page_columns_right lt-fyf-result-page_columns_right--stretch'>
            <div class='lt-fyf-result-page_next_steps_content'>
              <p class='lt-fyf-result-page_scbs_calculator_para'>
                <RenderTemplate :string="copy.smallChangeBigSavings" />
              </p>
              <div class='ahc-small-change-link-container'>
                <a
                  href='https://lifetime.dev.ahc.uk.com/smallchange2/lifetime-new/'
                  class='ahc-small-change-link'
                >Try the tool</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { mapFields } from "vuex-map-fields";


export default {
  data () {
    return {
      readMoreAmountVisible: false,
      readMoreTargetVisible: false,
      ageAutoNumericOpts: {
        decimalPlaces: 0,
        wheelStep: 1,
      },
    };
  },
  computed: {
    copy() {
      return {
        inputsHeader: `Elit mollit exercitation aliqua`,
        targetHeader: `Eiusmod officia velit ipsum eu velit.`,
        target: `To afford your chosen lifestyle in retirement, ${this.isCohabiting ? 'you and your partner will' : 'you\'ll'} need in the region of`,
        readMoreTarget: `Your target figure is the amount of expenditure ${this.isCohabiting ? 'you and your partner will' : 'you\'ll'} need to cover your chosen lifestyle in retirement. This expenditure amount has been calculated based on research carried out by the PLSA as part of their 'Retirement Living Standards' initiative. See the <a href='#' @click.prevent="$store.commit('goToPage', 'disclaimer-and-assumptions')">Disclaimer and Assumptions</a> for more detail.`,
        figureAmountNeeded: `To reach this target you ${this.isCohabiting ? 'and your partner' : ''} should aim to be saving`,
        isOnTrack: `You are on track to reach your target.`,
        readMoreAmountNeeded: `
        ${
          this.isOnTrack ?
            `<h3>What does this mean?</h3>
            <p>Your expenditure target will be covered by your ${this.isCohabiting ? 'combined' : ''} State Pension payment of {{ $store.getters.statePension | currency(($store.state.config.currencySymbol)) }} a year and any existing pension savings. </p>`
          :
            `<h3>What does this mean?</h3>
            <p>This is the amount ${this.isCohabiting ? 'you and your partner' : 'you'} would need to save each month in order to have enough income to meet your net expenditure target of {{ $store.getters.targetIncome | currency($store.state.config.currencySymbol) }} per year.</p>
            <p>It is based on ${this.isCohabiting ? 'you and your partner' : 'you'} needing a gross annual income in retirement of {{ $store.getters.grossIncomeNeeded | currency($store.state.config.currencySymbol) }} + ${this.isCohabiting ? 'combined' : ''} State Pension of {{ $store.getters.statePension | currency(($store.state.config.currencySymbol)) }}.</p>
            <p>This figure is a total ${this.isCohabiting ? "'household'" : ''} savings amount, so could also be made up of any pension contributions from your employer. It also assumes you will be entitled to the full State Pension when you retire.</p>`
        }

        <p>See the <a href='#' @click.prevent="$store.commit('goToPage', 'disclaimer-and-assumptions')">Disclaimer and Assumptions</a> for more detail.</p>`,
        smallChangeBigSavings: `<h3>What next?</h3>It can be easier than you think to save more money for your retirement. Explore your options using our Small Change, Big Savings calculator.`,
      }
    },

    ...mapState([
      'config',
    ]),
    ...mapFields({
      age: 'config.inputs.age.currentValue',
      retirementAge: 'config.inputs.retirementAge.currentValue',
      currentBalance: 'config.inputs.currentBalance.currentValue',
      isCohabiting: 'engine.isCohabiting',
    }),
    ...mapGetters([
      'areInputsValid',
      'targetIncome',
      'resultPerMonth',
      'standardChosen',
      'isAgeValid',
      'isRetirementAgeValid',
      'isCurrentBalanceValid',
      'maxTargetIncome',
      'standards',
      'isOnTrack',
      'minRetirementAge'
    ]),

    currentAgeAutoNumericOpts: function () {
      return _.defaults({
        minimumValue: 0,
        maximumValue: this.config.inputs.age.max,
      }, this.ageAutoNumericOpts);
    },

    retirementAgeAutoNumericOpts: function () {
      return _.defaults({
        minimumValue: 0,
        maximumValue: this.config.inputs.retirementAge.max,
      }, this.ageAutoNumericOpts);
    },

    currentBalanceAutoNumericOpts: function () {
      return {
        currencySymbol: this.config.currencySymbol,
        minimumValue: '0',
        allowDecimalPadding: false,
        emptyInputBehavior: 'min',
        minimumValue: this.config.inputs.currentBalance.min,
        maximumValue: this.config.inputs.currentBalance.max,
      }
    },
    triangleBarLeftCSSValue() {
      const range = this.standards.comfortable.maxValue - this.standards.minimum.maxValue;
      const percentage = ((this.targetIncome - this.standards.minimum.maxValue) / range * 100);
      return percentage;
    },
  },
  methods: {
    toggleBool(bool) {
      if (this[bool]) {
        this[bool] = false;
        return
      }
      this[bool] = true;
    },
    reload () {
      window.location.reload();
    },
    handleReadMoreClick(key, ref) {
      this.toggleBool([key]);
      if (this[key]) {
        this.$nextTick(() => {
          this.$refs[ref].focus(); // for accessibility
        });
      }
    }
  }
};
</script>

<style lang='scss'>

.lt-fyf-result-page {
  flex: 1 1 1px;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.lt-fyf-result-page_content_container {
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0 1rem;
}

.lt-fyf-result-page_standard-chosen {
  font-weight: normal;
  background: $main-background;
  color: $main-color;
  margin: 0.5rem 0;
  padding: 1rem;
  text-align: center;
  border-radius: 3px;
  font-size: 1rem;
  box-sizing: content-box;
  border-radius: 0.5rem;
  display: flex;
  justify-content: space-between;
}

.lt-fyf-result-page_standard-chosen_main {
  text-transform: uppercase;
  font-size: 1.6rem;
  font-weight: bold;
}

.lt-fyf-result-page_columns {
  position: relative;
  @include breakpoint-landscape {
    display: flex;
  }
}

.lt-fyf-result-page_top_content,
.lt-fyf-result-page_next_steps_content {
  text-align: left;
  background: $results-bg-color-1;
  color: $results-color-1;
  padding: 2rem;
  font-size: 1.2rem;
}
.lt-fyf-result-page_next_steps_content {
  border-radius: 0.5rem;
}

figure.lt-fyf-result-page_target_figure {
  @include breakpoint-portrait {
    padding: 0.625rem 0;
  }
  margin: 0;
  .animated-number {
    font-weight: bold;
    font-size: 2rem;
  }
}

.lt-fyf-result-page_target_button,
.lt-fyf-result-page_amount_needed_button {
  color: $results-color-2;
  display: block;
  padding: 0.5rem 0.75rem;
  background: #316bac;
  text-decoration: none;
  border-radius: 3px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  border: none;
  position: relative;
  margin-top: 1rem;
  cursor: pointer;
}

.lt-fyf-result-page_target_button {
  margin-top: 0;
}

.lt-fyf-result-page_amount_needed_button {
  position: absolute;
  left: 0;
  bottom: 0;
}

.lt-fyf-result-page_content {
  background: $results-bg-color-2;
  color: $results-color-2;
  box-shadow: 0 7px 5px 0px rgba(0, 0, 0, 0.25);
  border-radius: 3px 3px 3px 3px;
  padding: 1.5rem 2rem;
}

.lt-fyf-result-page_top_content {
  font-size: 1rem;
}

.lt-fyf-result-page_content {
  font-size: 1rem;
  .lt-fyf-result-page_columns_right {
    display: flex;
    align-items: center;
  }
  .lt-fyf-result-page_columns_right--stretch {
    display: flex;
    align-items: stretch;
  }
}

.lt-fyf-result-page_next_steps_content {
  font-size: 1rem;
  display: flex;
  flex-direction: column;
  .lt-fyf-result-page_columns_right {
    padding: 1.5625rem 0;
    @include breakpoint-landscape {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      padding: 0;
    }
  }
  p {
    margin-bottom: 1rem;
  }
}

.lt-fyf-result-page_inputs {
  // max-width: 31.25rem;
  margin-top: 0;
  margin-bottom: 0;
}

.lt-fyf-result-page_columns_left {
  flex: 1;
  padding: 0;
  width: 100%;
}

.lt-fyf-result-page_columns_right {
  flex: 0.4;
  width: 100%;
  position: relative;
  @include breakpoint-landscape {
    margin-left: 6rem;
  }
}
.lt-fyf-result-page_columns_right--stretch {
  @include breakpoint-landscape {
    margin-left: 2rem;
  }
}

.lt-fyf-pd-field {
  display: flex;
  font-size: 1.2rem;
  margin: 0.5rem 0;
  align-items: center;
  label {
    flex: 1;
    margin-right: 1.25rem;
    @include breakpoint-mobile {
      font-size: 1rem;
    }
  }
  input {
    font-size: 1.4rem;
    max-width: 8.75rem;
    width: 100%;
    font-weight: bold;
    text-align: center;
    padding: 0.25rem 0;
    border: 0 none;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 3px;
    color: $results-color-2;
    &[type=number]::-webkit-inner-spin-button,
    &[type=number]::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    &[disabled] {
      background: rgba(0, 0, 0, 0.06);
    }
    @include breakpoint-mobile {
      max-width: 6.75rem;
    }
  }

  &.lt-fyf-pd-field--is-error {
    input {
      background: rgba(255, 0, 0, 0.3);
    }
  }
}


.lt-fyf-result-page_target-pm {
  font-size: 1.2rem;
  margin: 2rem 0;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  @include breakpoint-portrait {
    padding: 1.5625rem 0;
  }
}
.lt-fyf-result-page_target-pm_text {
  min-width: 0;
  flex: 1;
  max-width: 330px;
  @include breakpoint-portrait {
    min-width: 100%;
    padding-bottom: 1rem;
  }
}
figure.lt-fyf-result-page_target-pm_figure {
  margin: 0;
  flex: 1;
  text-align: right;
  span {
    display: inline-block;
    margin-left: 0.5rem;
  }
  @include breakpoint-portrait {
    text-align: left;
  }
  .animated-number {
    font-weight: bold;
    font-size: 2.6rem;
  }
}

.refresh-button {
  appearance: none;
  background: transparent;
  color: $results-color-2;
  border: 0 none;
  padding: 0.75rem;
  padding-left: 1.75rem;
  background: transparent url('../assets/start-over.svg') no-repeat center left;
  background-size: 1.125rem;
  margin-top: 1rem;
  cursor: pointer;
  @include breakpoint-portrait {
    display: none;
  }
}

.lt-fyf-result-page_top_content_header {
  margin: 0;
  color: $header-color-1;
}

.lt-fyf-result-page_top_content_main-para {
  margin: 0;
}

.lt-fyf-result-page_scbs_calculator_para {
  margin: 0;
}

.target-bar {
  width: calc(100% - 2rem);
  position: relative;
  // height: 10px;
  // background: black;
  margin-left: auto;
  margin-right: auto;
  .target-triangle {
    $triangle-size: 1.375rem;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 $triangle-size $triangle-size $triangle-size;
    border-color: transparent transparent $results-bg-color-1 transparent;
    position: absolute;
    top: 0;
    margin-top: -$triangle-size;
  }
}

.standard-values {
  text-transform: uppercase;
  display: flex;
  justify-content: center;

  @include breakpoint-mobile {
    flex-direction: column;
  }

  &__text {
    font-weight: bold;
  }

  &__text, &__figure {
    margin: 0 3px;
  }

}

.large-arrow {
  display: block;
  background-image: url("../assets/large-arrow.svg");
  width: 70px;
  background-size: 70px 70px;
  background-repeat: no-repeat;
  background-position: center;
  @include breakpoint-landscape {
  }
}

.lt-fyf-result-page_columns_left.middle-box {
  display: flex;
  .lt-fyf-result-page_inputs {
    flex: 1 1 auto;
  }
}

.lt-fyf-result-page_read-more_amount-needed_content {
  padding: 0.5rem 1rem;
  background: $results-bg-color-3;
  color: $results-color-3;
  border-radius: 1rem;
  margin-top: 2rem;
  position: relative;
  @include breakpoint-landscape {
    display: flex;
    justify-content: flex-start;
  }
}

.lt-fyf-result-page_read-more_amount-needed_content:after {
  content: '';
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 16px 16px 16px;
  border-color: transparent transparent $results-bg-color-3 transparent;
  top: -1rem;
  left: 90px;
  @include breakpoint-landscape {
    left: auto;
    right: 90px;
  }
}


.lt-fyf-result-page_read-more_target_content {
  margin-top: 1rem;
  @include breakpoint-landscape {
    p { 
      max-width: 31.25rem;
      display: inline-block;
      margin-top: 2rem;
    }
  }
}

.lt-fyf-result-page_seperator {
  border: 0;
  background: rgba(47, 73, 89, 0.9);
  height: 1px;
  position: absolute;
  top: 1rem;
  left: 0;
  right: 0;
  bottom: 0;
  @include breakpoint-portrait {
    top: -0.9375rem;
  }
}

.lt-fyf-result-page_columns_right.read-more-container {
  display: flex;
  align-items: flex-end;
}

// height animation for readMore buttons
.height-enter-active,
.height-leave-active {
  transition: all 0.2s;
  max-height: 375rem;
}

.height-enter,
.height-leave-to {
  opacity: 0;
  max-height: 0rem;
}

.lt-fyf-result-overview {
  min-height: 0;
  display: flex;
  margin-bottom: 0;
  align-items: center;
  flex-wrap: wrap;
}
.lt-fyf-result-overview_left {
  flex: 0.75;
  display: inline-block;
  @include breakpoint-portrait {
    min-width: 100%;
  }
}
.lt-fyf-result-overview_center {
  flex: 1;
  display: inline-block;
  text-align: right;
  @include breakpoint-portrait {
    min-width: 100%;
    text-align: left;
  }
}
.lt-fyf-result-overview_right {
  flex: 0.25;
  display: inline-block;
  text-align: right;
  margin-left: 6rem;
  @include breakpoint-portrait {
    min-width: 100%;
    text-align: left;
    margin-left: 0;
  }
}
</style>
